import { generateSpeech as generateSpeechAPI } from './voiceapi';

export interface TTSOptions {
  voice: string;
  messageId?: string;
}

export interface TTSPlaybackState {
  isPlaying: boolean;
  isConverting: boolean;
  currentMessageId: string | null;
  currentTime: number;
  duration: number;
  isPaused: boolean;
  /** Playback reached the end; resuming replays from the start. */
  hasFinished: boolean;
}

const MAX_TTS_LENGTH = 5000;
const MAX_CACHE_ENTRIES = 20;

const GENERATE_ERROR = "Couldn't Generate Audio. Please Try Again.";
const PLAY_ERROR = "Couldn't Play Audio. Please Try Again.";
const EMPTY_TEXT_ERROR = "There's Nothing to Read Aloud in This Message.";

/** A few samples of silence, played during the click to unlock the element for later playback. */
const SILENT_AUDIO =
  'data:audio/wav;base64,UklGRjQAAABXQVZFZm10IBAAAAABAAEAQB8AAIA+AAACABAAZGF0YRAAAAAAAAAAAAAAAAAAAAAAAAAA';

const INITIAL_STATE: TTSPlaybackState = {
  isPlaying: false,
  isConverting: false,
  currentMessageId: null,
  currentTime: 0,
  duration: 0,
  isPaused: false,
  hasFinished: false
};

const isAbortError = (error: unknown): boolean =>
  error instanceof Error && error.name === 'AbortError';

class TTSService {
  /** Generated audio keyed by voice + text; insertion order doubles as LRU order. */
  private cache = new Map<string, Blob>();
  /**
   * One element reused for every playback. Browsers (Safari especially) only allow `play()`
   * during a user gesture, and generation outlives the click — so the element is unlocked
   * in the click and then reused once the audio arrives.
   */
  private audio: HTMLAudioElement | null = null;
  private currentAudioUrl: string | null = null;
  private abortController: AbortController | null = null;
  /** Bumped by every speak/stop so stale async work can tell it has been superseded. */
  private requestId = 0;
  private playbackState: TTSPlaybackState = { ...INITIAL_STATE };
  private stateUpdateCallback?: (state: TTSPlaybackState) => void;
  private errorCallback?: (message: string) => void;

  /**
   * Sanitizes and formats text for optimal text-to-speech output
   * - Removes code blocks, math notation, and technical formatting
   * - Converts markdown to plain text
   * - Removes HTML and special tags
   * - Optimizes punctuation and spacing for natural speech flow
   */
  private sanitizeText(text: string): string {
    if (!text || typeof text !== 'string') {
      return '';
    }
    
    let processed = text;
    
    // Remove resource viewing tags
    processed = processed.replace(/<resource_viewing>.*?<\/resource_viewing>/gs, '');
    
    // Remove quote tags but keep content
    processed = processed.replace(/<\/?quote>/g, '');
    
    // Remove code blocks (they don't speak well)
    processed = processed.replace(/```[\s\S]*?```/g, ' [code block omitted] ');
    
    // Remove inline code backticks
    processed = processed.replace(/`([^`]+)`/g, '$1');
    
    // Remove LaTeX math notation (both inline and block)
    processed = processed.replace(/\$\$[\s\S]*?\$\$/g, ' [mathematical equation] ');
    // Inline math must start with a non-digit so currency ("$5 and $10") survives.
    processed = processed.replace(/\$([^\s\d$][^$\n]*?)\$/g, (match, inner: string) =>
      /\s$/.test(inner) ? match : ' [math] '
    );
    processed = processed.replace(/\\(?:\[|\()[\s\S]*?\\(?:\]|\))/g, ' [equation] ');
    
    // Convert markdown headers to readable text
    processed = processed.replace(/^#{1,6}\s+(.+)$/gm, '$1. ');
    
    // Convert markdown bold and italic to plain text
    processed = processed.replace(/\*\*\*(.+?)\*\*\*/g, '$1');
    processed = processed.replace(/\*\*(.+?)\*\*/g, '$1');
    processed = processed.replace(/\*(.+?)\*/g, '$1');
    // Underscore emphasis only at word boundaries, so snake_case identifiers stay intact.
    processed = processed.replace(/(^|\W)___(\S(?:.*?\S)?)___(?=\W|$)/g, '$1$2');
    processed = processed.replace(/(^|\W)__(\S(?:.*?\S)?)__(?=\W|$)/g, '$1$2');
    processed = processed.replace(/(^|\W)_(\S(?:.*?\S)?)_(?=\W|$)/g, '$1$2');
    
    // Convert markdown links to just the text
    processed = processed.replace(/\[([^\]]+)\]\([^)]+\)/g, '$1');
    
    // Remove remaining HTML tags
    processed = processed.replace(/<[^>]*>/g, ' ');
    
    // Remove potential script injections
    processed = processed.replace(/javascript:/gi, '');
    
    // Convert list markers to pauses
    processed = processed.replace(/^[\s]*[-*+]\s+/gm, '. ');
    processed = processed.replace(/^[\s]*\d+\.\s+/gm, '. ');
    
    // Remove multiple consecutive punctuation marks
    processed = processed.replace(/([.!?])\1+/g, '$1');
    
    // Add pauses after sentences for better speech flow
    processed = processed.replace(/([.!?])\s+/g, '$1 ');
    
    // Normalize whitespace (but preserve single newlines as pauses)
    processed = processed.replace(/\n{3,}/g, '\n\n');
    processed = processed.replace(/\n/g, '. ');
    processed = processed.replace(/\s+/g, ' ');
    processed = processed.trim();
    
    // Remove any remaining special characters that don't speak well
    processed = processed.replace(/[\u200B-\u200D\uFEFF]/g, '');

    if (processed.length > MAX_TTS_LENGTH) {
      processed = `${processed.substring(0, MAX_TTS_LENGTH)}...`;
    }

    return processed;
  }


  private getCacheKey(text: string, voice: string): string {
    return `${voice}-${text}`;
  }

  private updateState(updates: Partial<TTSPlaybackState>): void {
    this.playbackState = { ...this.playbackState, ...updates };
    this.stateUpdateCallback?.(this.playbackState);
  }

  private reportError(message: string): void {
    this.errorCallback?.(message);
  }

  /** Events only count while the element is playing our generated audio, not the unlock clip. */
  private isCurrentSource(audio: HTMLAudioElement): boolean {
    return this.currentAudioUrl !== null && audio.src === this.currentAudioUrl;
  }

  private getAudioElement(): HTMLAudioElement {
    if (this.audio) return this.audio;

    const audio = new Audio();
    audio.preload = 'auto';

    audio.addEventListener('ended', () => {
      if (!this.isCurrentSource(audio)) return;
      this.updateState({ isPaused: true, hasFinished: true, currentTime: audio.duration || 0 });
    });

    audio.addEventListener('error', () => {
      if (!this.isCurrentSource(audio)) return;
      console.error('Audio playback failed:', audio.error);
      this.stop();
      this.reportError(PLAY_ERROR);
    });

    audio.addEventListener('timeupdate', () => {
      if (!this.isCurrentSource(audio)) return;
      this.updateState({ currentTime: audio.currentTime, duration: audio.duration || 0 });
    });

    audio.addEventListener('durationchange', () => {
      if (!this.isCurrentSource(audio)) return;
      this.updateState({ duration: audio.duration || 0 });
    });

    this.audio = audio;
    return audio;
  }

  /** Must run synchronously inside the user's click, before anything is awaited. */
  private unlockAudio(): void {
    const audio = this.getAudioElement();
    audio.src = SILENT_AUDIO;
    audio.play()?.catch(() => {
      // Not in a user gesture, or interrupted by the real audio; either way nothing to do.
    });
  }

  private async getAudioBlob(text: string, voice: string, signal: AbortSignal): Promise<Blob> {
    const key = this.getCacheKey(text, voice);
    const cached = this.cache.get(key);
    if (cached) {
      this.cache.delete(key);
      this.cache.set(key, cached);
      return cached;
    }

    const { audio } = await generateSpeechAPI(text, { id: voice }, signal);
    this.cache.set(key, audio);
    if (this.cache.size > MAX_CACHE_ENTRIES) {
      const oldest = this.cache.keys().next().value;
      if (oldest !== undefined) this.cache.delete(oldest);
    }
    return audio;
  }

  private releaseCurrentUrl(): void {
    if (this.currentAudioUrl) {
      URL.revokeObjectURL(this.currentAudioUrl);
      this.currentAudioUrl = null;
    }
  }

  public onStateUpdate(callback: (state: TTSPlaybackState) => void): void {
    this.stateUpdateCallback = callback;
  }

  /** Receives user-facing error messages (already title-cased for toasts). */
  public onError(callback: (message: string) => void): void {
    this.errorCallback = callback;
  }

  public async speak(text: string, options: TTSOptions): Promise<void> {
    this.stop();
    this.unlockAudio();
    const requestId = ++this.requestId;

    const sanitizedText = this.sanitizeText(text);
    if (!sanitizedText) {
      this.reportError(EMPTY_TEXT_ERROR);
      return;
    }

    const controller = new AbortController();
    this.abortController = controller;
    this.updateState({
      ...INITIAL_STATE,
      isConverting: true,
      currentMessageId: options.messageId || null
    });

    let blob: Blob;
    try {
      blob = await this.getAudioBlob(sanitizedText, options.voice, controller.signal);
    } catch (error) {
      if (requestId !== this.requestId || isAbortError(error)) return;
      console.error('TTS generation error:', error);
      this.stop();
      this.reportError(GENERATE_ERROR);
      return;
    }

    // A newer speak or a stop replaced this request while it was generating.
    if (requestId !== this.requestId) return;
    this.abortController = null;

    const audio = this.getAudioElement();
    const url = URL.createObjectURL(blob);
    this.currentAudioUrl = url;
    audio.src = url;
    this.updateState({ isConverting: false, isPlaying: true });

    try {
      await audio.play();
    } catch (error) {
      if (requestId !== this.requestId || isAbortError(error)) return;
      console.error('Failed to start audio playback:', error);
      this.stop();
      this.reportError(PLAY_ERROR);
    }
  }

  public stop(): void {
    this.requestId++;
    this.abortController?.abort();
    this.abortController = null;

    if (this.audio) {
      this.audio.pause();
      this.audio.removeAttribute('src');
      this.audio.load();
    }

    this.releaseCurrentUrl();
    this.updateState({ ...INITIAL_STATE });
  }

  public pause(): void {
    if (this.audio && this.playbackState.isPlaying && !this.playbackState.isPaused) {
      this.audio.pause();
      this.updateState({ isPaused: true });
    }
  }

  /** Resumes a paused message, or replays it from the start once it has finished. */
  public resume(): void {
    const audio = this.audio;
    if (!audio || !this.playbackState.isPlaying || !this.playbackState.isPaused) return;

    if (this.playbackState.hasFinished) {
      audio.currentTime = 0;
    }
    audio.play().catch(error => {
      if (isAbortError(error)) return;
      console.error('Failed to resume audio playback:', error);
      this.stop();
      this.reportError(PLAY_ERROR);
    });
    this.updateState({ isPaused: false, hasFinished: false });
  }

  public getState(): TTSPlaybackState {
    return { ...this.playbackState };
  }

  public clearCache(): void {
    this.cache.clear();
  }

  public getCacheSize(): number {
    return this.cache.size;
  }

  public cleanup(): void {
    this.stop();
    this.clearCache();
    this.stateUpdateCallback = undefined;
    this.errorCallback = undefined;
  }
}

export const ttsService = new TTSService();
